import { useEffect, useState } from "react";
import {
  CalendarPlus,
  Check,
  PackageCheck,
  Search,
  UsersRound,
} from "lucide-react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input, Label, Select } from "@/components/ui/field";
import type {
  DistributionService,
  DistributionSession,
  DistributionVisit,
  Profile,
} from "@/shared/types";

interface DistributionVisitWithProfile extends DistributionVisit {
  profile: Profile | null;
}

const statusClass = (status: DistributionSession["status"]) =>
  status === "open" ? "bg-secondary text-primary" : "";

export function DistributionsAdminPage() {
  const [sessions, setSessions] = useState<DistributionSession[]>([]);
  const [selected, setSelected] = useState<DistributionSession | null>(null);
  const [visits, setVisits] = useState<DistributionVisitWithProfile[]>([]);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<Profile[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchComplete, setSearchComplete] = useState(false);
  const [neighbor, setNeighbor] = useState<Profile | null>(null);
  const [householdSize, setHouseholdSize] = useState(1);
  const [services, setServices] = useState<DistributionService[]>(["food"]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const loadSessions = async () => {
    const loaded = await api<DistributionSession[]>(
      "/admin/distributions?location_id=mountain-view",
    );
    setSessions(loaded);
    return loaded;
  };

  const open = async (session: DistributionSession) => {
    setSelected(session);
    setVisits(
      await api<DistributionVisitWithProfile[]>(
        `/admin/distributions/${session.id}/visits`,
      ),
    );
    setSearch("");
    setResults([]);
    setNeighbor(null);
    setMessage("");
    setError("");
  };

  useEffect(() => {
    void loadSessions();
  }, []);

  useEffect(() => {
    if (!selected || search.trim().length < 2 || neighbor) {
      setResults([]);
      setSearching(false);
      setSearchComplete(false);
      return;
    }
    let active = true;
    setSearching(true);
    setSearchComplete(false);
    const timer = window.setTimeout(() => {
      const query = new URLSearchParams({
        relationship: "neighbor",
        search: search.trim(),
        limit: "12",
        offset: "0",
      });
      void api<Profile[]>(`/admin/profiles?${query}`)
        .then((profiles) => {
          if (active) setResults(profiles);
        })
        .catch(() => {
          if (active) setResults([]);
        })
        .finally(() => {
          if (active) {
            setSearching(false);
            setSearchComplete(true);
          }
        });
    }, 250);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [search, selected, neighbor]);

  const create = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const data = new FormData(event.currentTarget);
      const startsAt = new Date(String(data.get("starts_at")));
      const session = await api<DistributionSession>("/admin/distributions", {
        method: "POST",
        body: JSON.stringify({
          name: data.get("name"),
          location_id: "mountain-view",
          starts_at: startsAt.toISOString(),
          ends_at: new Date(
            startsAt.getTime() + 2 * 60 * 60 * 1000,
          ).toISOString(),
          timezone: "America/Los_Angeles",
          venue_name: data.get("venue_name"),
          status: data.get("status"),
        }),
      });
      setCreating(false);
      await loadSessions();
      await open(session);
    } catch (caught: unknown) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Unable to create the distribution.",
      );
    } finally {
      setBusy(false);
    }
  };

  const updateStatus = async (status: DistributionSession["status"]) => {
    if (!selected) return;
    setBusy(true);
    setError("");
    try {
      const updated = await api<DistributionSession>(
        `/admin/distributions/${selected.id}`,
        {
          method: "PATCH",
          body: JSON.stringify({ status }),
        },
      );
      setSelected(updated);
      setSessions((all) =>
        all.map((session) => (session.id === updated.id ? updated : session)),
      );
    } catch (caught: unknown) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Unable to update the distribution.",
      );
    } finally {
      setBusy(false);
    }
  };

  const chooseNeighbor = (profile: Profile) => {
    setNeighbor(profile);
    setHouseholdSize(profile.household_size || 1);
    setResults([]);
    setMessage("");
    setError("");
  };

  const toggleService = (service: DistributionService) => {
    setServices((current) =>
      current.includes(service)
        ? current.filter((item) => item !== service)
        : [...current, service],
    );
  };

  const checkIn = async () => {
    if (!selected || !neighbor || !services.length) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const result = await api<{
        visit: DistributionVisit;
        created: boolean;
        profile: Profile;
        session: DistributionSession;
      }>(`/admin/distributions/${selected.id}/check-ins`, {
        method: "POST",
        body: JSON.stringify({
          profile_id: neighbor.id,
          household_size: householdSize,
          services,
        }),
      });
      setSelected(result.session);
      setSessions((all) =>
        all.map((session) =>
          session.id === result.session.id ? result.session : session,
        ),
      );
      if (result.created) {
        setVisits((all) => [
          { ...result.visit, profile: result.profile },
          ...all,
        ]);
        setMessage(`${result.profile.first_name} checked in successfully.`);
      } else {
        setMessage(`${result.profile.first_name} was already checked in.`);
      }
      setNeighbor(null);
      setSearch("");
      setServices(["food"]);
      setHouseholdSize(1);
    } catch (caught: unknown) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Unable to check in this household.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Distributions</h1>
          <p className="mt-2 text-muted-foreground">
            Schedule service sessions and record every household served.
          </p>
        </div>
        <Button onClick={() => setCreating((value) => !value)}>
          <CalendarPlus size={17} />
          New distribution
        </Button>
      </div>

      {creating && (
        <Card className="mt-6">
          <CardContent className="p-6">
            <form onSubmit={create} className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="distribution-name" className="mb-2 block">
                  Name
                </Label>
                <Input
                  id="distribution-name"
                  name="name"
                  defaultValue="Food distribution"
                  required
                />
              </div>
              <div>
                <Label htmlFor="distribution-start" className="mb-2 block">
                  Starts
                </Label>
                <Input
                  id="distribution-start"
                  name="starts_at"
                  type="datetime-local"
                  required
                />
              </div>
              <div>
                <Label htmlFor="distribution-venue" className="mb-2 block">
                  Venue
                </Label>
                <Input
                  id="distribution-venue"
                  name="venue_name"
                  defaultValue="Hope Hangar Mountain View"
                  required
                />
              </div>
              <div>
                <Label htmlFor="distribution-status" className="mb-2 block">
                  Initial status
                </Label>
                <Select
                  id="distribution-status"
                  name="status"
                  defaultValue="scheduled"
                >
                  <option value="scheduled">Scheduled</option>
                  <option value="open">Open for check-in</option>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Button type="submit" disabled={busy}>
                  {busy ? "Creating…" : "Create distribution"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {error && (
        <p role="alert" className="mt-5 rounded-lg bg-red-50 p-4 text-red-800">
          {error}
        </p>
      )}

      <div className="mt-7 grid gap-6 lg:grid-cols-[340px_1fr]">
        <div className="space-y-3">
          {sessions.map((session) => (
            <button
              key={session.id}
              type="button"
              onClick={() => void open(session)}
              className={`w-full rounded-xl border bg-card p-4 text-left shadow-sm transition-colors hover:border-primary/40 ${
                selected?.id === session.id ? "border-primary" : ""
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <strong>{session.name}</strong>
                <Badge className={statusClass(session.status)}>
                  {session.status}
                </Badge>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                {new Date(session.starts_at).toLocaleString()}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {session.households_served} households · {session.people_served}{" "}
                people
              </p>
            </button>
          ))}
          {!sessions.length && (
            <p className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">
              No distributions scheduled yet.
            </p>
          )}
        </div>

        {selected ? (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <CardTitle>{selected.name}</CardTitle>
                    <p className="mt-2 text-sm text-muted-foreground">
                      {new Date(selected.starts_at).toLocaleString()} ·{" "}
                      {selected.venue_name}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {selected.status !== "open" && (
                      <Button
                        size="sm"
                        disabled={busy}
                        onClick={() => void updateStatus("open")}
                      >
                        Open check-in
                      </Button>
                    )}
                    {selected.status !== "closed" && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        onClick={() => void updateStatus("closed")}
                      >
                        Close
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-xl bg-secondary p-4">
                    <UsersRound size={20} />
                    <strong className="mt-3 block text-2xl">
                      {selected.households_served}
                    </strong>
                    <span className="text-sm text-muted-foreground">
                      households served
                    </span>
                  </div>
                  <div className="rounded-xl bg-secondary p-4">
                    <PackageCheck size={20} />
                    <strong className="mt-3 block text-2xl">
                      {selected.people_served}
                    </strong>
                    <span className="text-sm text-muted-foreground">
                      people served
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Household check-in</CardTitle>
              </CardHeader>
              <CardContent>
                {selected.status === "closed" ? (
                  <p className="text-sm text-muted-foreground">
                    This distribution is closed. Reopen it to check in another
                    household.
                  </p>
                ) : (
                  <div className="space-y-5">
                    {!neighbor ? (
                      <div>
                        <Label htmlFor="neighbor-search" className="mb-2 block">
                          Find a Neighbor
                        </Label>
                        <div className="relative">
                          <Search
                            size={18}
                            className="absolute left-3 top-3 text-muted-foreground"
                          />
                          <Input
                            id="neighbor-search"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder="Search name, email, or phone"
                            autoComplete="off"
                            className="pl-10"
                          />
                        </div>
                        {search.trim().length === 1 && (
                          <p className="mt-2 text-xs text-muted-foreground">
                            Enter at least two characters.
                          </p>
                        )}
                        {searching && (
                          <p className="mt-3 text-sm text-muted-foreground">
                            Searching…
                          </p>
                        )}
                        {results.length > 0 && (
                          <div className="mt-3 divide-y rounded-lg border">
                            {results.map((profile) => (
                              <button
                                key={profile.id}
                                type="button"
                                className="flex w-full items-center justify-between gap-3 p-3 text-left hover:bg-muted"
                                onClick={() => chooseNeighbor(profile)}
                              >
                                <span>
                                  <strong className="block text-sm">
                                    {profile.first_name} {profile.last_name}
                                  </strong>
                                  <span className="text-xs text-muted-foreground">
                                    {profile.phone || profile.email}
                                  </span>
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  Household {profile.household_size || "—"}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                        {searchComplete &&
                          search.trim().length >= 2 &&
                          !results.length && (
                            <p className="mt-3 text-sm text-muted-foreground">
                              No matching Neighbor found. Use Neighbor intake to
                              add a first-time household.
                            </p>
                          )}
                      </div>
                    ) : (
                      <div className="rounded-xl border bg-muted/30 p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <strong>
                              {neighbor.first_name} {neighbor.last_name}
                            </strong>
                            <p className="text-sm text-muted-foreground">
                              {neighbor.phone || neighbor.email}
                            </p>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setNeighbor(null);
                              setSearch("");
                            }}
                          >
                            Change
                          </Button>
                        </div>
                        <div className="mt-5 grid gap-5 sm:grid-cols-2">
                          <div>
                            <Label
                              htmlFor="household-size"
                              className="mb-2 block"
                            >
                              People receiving service
                            </Label>
                            <Input
                              id="household-size"
                              type="number"
                              min={1}
                              max={30}
                              value={householdSize}
                              onChange={(event) =>
                                setHouseholdSize(Number(event.target.value))
                              }
                            />
                          </div>
                          <fieldset>
                            <legend className="mb-2 text-sm font-medium">
                              Services provided
                            </legend>
                            <div className="space-y-2 text-sm">
                              {(
                                [
                                  ["food", "Food"],
                                  ["clothing", "Clothing"],
                                  ["other", "Other"],
                                ] as const
                              ).map(([value, label]) => (
                                <label
                                  key={value}
                                  className="flex items-center gap-2"
                                >
                                  <input
                                    type="checkbox"
                                    checked={services.includes(value)}
                                    onChange={() => toggleService(value)}
                                  />
                                  {label}
                                </label>
                              ))}
                            </div>
                          </fieldset>
                        </div>
                        <Button
                          className="mt-5 w-full sm:w-auto"
                          disabled={
                            busy || householdSize < 1 || !services.length
                          }
                          onClick={() => void checkIn()}
                        >
                          <Check size={17} />
                          {busy ? "Checking in…" : "Check in household"}
                        </Button>
                      </div>
                    )}
                    {message && (
                      <p
                        role="status"
                        className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800"
                      >
                        {message}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Households served</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {visits.map((visit) => (
                    <div
                      key={visit.id}
                      className="flex flex-wrap items-center justify-between gap-3 p-4"
                    >
                      <div>
                        <strong className="text-sm">
                          {visit.profile
                            ? `${visit.profile.first_name} ${visit.profile.last_name}`
                            : "Neighbor household"}
                        </strong>
                        <p className="text-xs text-muted-foreground">
                          {new Date(visit.checked_in_at).toLocaleTimeString()} ·{" "}
                          {visit.services.join(", ")}
                        </p>
                      </div>
                      <Badge>{visit.household_size} people</Badge>
                    </div>
                  ))}
                  {!visits.length && (
                    <p className="p-8 text-center text-sm text-muted-foreground">
                      No households checked in yet.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <Card className="h-fit">
            <CardContent className="p-8 text-center text-muted-foreground">
              Select a distribution to open check-in and review service totals.
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
